import Button from "@/components/bricks/button/Button";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { TextInputFormik } from "../../formcontrols/InputItem";
import { WizardReceipt } from "../common/wizardReceipt";
import AutoCompletePerson from "../../autocomplete/AutoCompletePerson";
import {
  TicketHeading,
  TicketRoot,
  TicketSub,
  TicketSubItem,
} from "../../ticket/Ticket";
import FirmaIcon from "../../icons/FirmaIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import { useFuldmaegtigAutorisation } from "./useFuldmaegtigAutorisation";
import { useContext, useState } from "react";
import { ModalContext } from "../../Modal/Modal";
import { useNavigate } from "react-router-dom";
import InputRadio from "../../formcontrols/InputRadio";
import { format } from "date-fns";
import ValidationErrorMessage from "../../formcontrols/ValidationErrorMessage";
import { AutoCompleteFormSelectedItem } from "../../autocomplete/AutoCompleteListItem";
import AutoCompleteFirma from "../../autocomplete/AutoCompleteFirma";
import { FirmaRelationKodeDict } from "../OpretPerson/useOpretPerson";
import { PersonDto } from "../../../interfaces/schema";
import IsRequired from "../../formcontrols/common/IsRequired";
import { extractCurrentPersonFirma } from "../../../util/person";
import Spinner from "../../spinner/Spinner";
import InputCheckbox from "../../formcontrols/InputCheckbox";
import usePersonCprNumber from "../../../hooks/usePersonCprNumber";
import { cprAdgangInfo, canEditFoedselsDato } from "../../../util/cprAccess";
import { AlertMessage } from "../../AlertMessage";

interface IFuldmaegtigAutorisationWizardProps {
  person: PersonDto;
  refetch: InvalidateDataFunction<any>;
}

interface IFuldmaegtigAutorisationWizardWithCprProps extends IFuldmaegtigAutorisationWizardProps {
  cpr: string;
  invalidateCpr: InvalidateDataFunction<any>;
  canReadCpr: boolean;
}

export default function FuldmaegtigAutorisationWizardCprWrapper(
  props: IFuldmaegtigAutorisationWizardProps,
) {
  const { canReadCpr, cpr, cprLoading, invalidateCpr } = usePersonCprNumber(
    props.person.id,
    true,
  );
  if (cprLoading) return <Spinner size={24} />;
  // ADV-349: uden CprRead hentes CPR-nummeret ikke; wizarden kører uden løbenummer.
  if (canReadCpr && cpr === undefined) return <p>CPR-kald fejlet</p>;
  return (
    <FuldmaegtigAutorisationWizard
      {...props}
      cpr={cpr ?? ""}
      invalidateCpr={invalidateCpr}
      canReadCpr={canReadCpr}
    />
  );
}

function FuldmaegtigAutorisationWizard({
  person,
  refetch,
  cpr,
  invalidateCpr,
  canReadCpr,
}: IFuldmaegtigAutorisationWizardWithCprProps) {
  const currentFirma = extractCurrentPersonFirma(person);
  const {
    formik,
    isLoading,
    postError,
    isPatchLoading,
    patchError,
    postData,
    patchData,
    patchAttempted,
    aktiveFirmaRelationer,
    futureFirmaRelationer,
  } = useFuldmaegtigAutorisation(person, currentFirma, cpr, canReadCpr);
  const { setTouched, handleSubmit, setFieldValue, isSubmitting, values } =
    formik;
  const { setModalOpen } = useContext(ModalContext);
  const navigate = useNavigate();
  const [
    principalInputShouldCaptureFocus,
    setPrincipalInputShouldCaptureFocus,
  ] = useState(false);
  const [firmaInputShouldCaptureFocus, setFirmaInputShouldCaptureFocus] =
    useState(false);
  if (patchError || postError) {
    return (
      <div className="c-modal-container">
        <WizardReceipt heading="Noget gik galt" icon="warning">
          <p className="text-muted text-center">
            {JSON.stringify(
              (patchError as any)?.data ?? (postError as any)?.data,
            )}
          </p>
          <p className="text-muted text-center">Luk for at afslutte</p>
        </WizardReceipt>
        <div className="flex-100 d-flex justify-content-center mt-3">
          <Button
            color="secondary"
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Luk
          </Button>
        </div>
      </div>
    );
  }

  if (postData && (!patchAttempted || patchData)) {
    invalidateCpr();
    return (
      <div className="c-modal-container">
        <WizardReceipt heading="Gennemført" icon="success">
          <p className="text-muted text-center">
            {person.fuldtNavn} er gjort fuldmægtig per{" "}
            {format(new Date(), "yyyy-MM-dd")} .
          </p>
        </WizardReceipt>
        <div className="flex-100 d-flex justify-content-center mt-3">
          <Button
            color="secondary"
            onClick={() => {
              setModalOpen(false);
              refetch();
            }}
          >
            Luk
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="c-modal-container">
      <form autoComplete="off" onSubmit={handleSubmit}>
        <fieldset disabled={isPatchLoading || isLoading || isSubmitting}>
          <div className="c-item-list c-modal-container-inner">
            <h3 className="mb-n3 flex-100">Fødselsdato</h3>
            <div className="flex-100 flex-row align-items-end gap-2">
              <div className="flex-grow-1 position-relative">
                <FormikDatePicker
                  formik={formik}
                  disabled={
                    !canEditFoedselsDato(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })
                  }
                  config={{
                    component: "datepicker",
                    id: "foedselsDato",
                    label: canReadCpr
                      ? "Fødselsdato* / CPR-nr."
                      : "Fødselsdato*",
                  }}
                />
                {cprAdgangInfo(canReadCpr, {
                  harCprNummer: !!person.harCprNummer,
                  foedselsDato: person.foedselsDato,
                }) && (
                  <AlertMessage type="info" className="mt-2">
                    {cprAdgangInfo(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })}
                  </AlertMessage>
                )}
              </div>
              {canReadCpr && (
                <div className="d-flex align-items-center gap-1">
                  <span>-</span>
                  <TextInputFormik
                    formik={formik}
                    config={{
                      component: "textinput",
                      id: "cprNr",
                      label: " ",
                      placeholder: "0000",
                      initialValue: "",
                    }}
                  />
                </div>
              )}
            </div>
            <h3 className="mb-n2 mt-3 flex-100">Principal</h3>
            <div className="flex-100 align-items-stretch">
              <label className="form-label">
                Principalnavn <IsRequired />
              </label>
              {!formik.values.principal && (
                <AutoCompletePerson
                  placeholder="Søg og vælg principal ..."
                  onSelectedItemChange={(item) => {
                    setPrincipalInputShouldCaptureFocus(true);
                    setTouched({ ...formik.touched, principal: true });
                    setFieldValue("principal", item.selectedItem);
                  }}
                  includeFirstItem={false}
                  erUdoevendeAdvokat={true}
                  focusInputOnMount={principalInputShouldCaptureFocus}
                />
              )}
              {formik.values.principal && (
                <AutoCompleteFormSelectedItem
                  title={"Fjern principal"}
                  item={formik.values.principal}
                  onClick={() => {
                    setPrincipalInputShouldCaptureFocus(true);
                    setFieldValue("principal", undefined);
                  }}
                  focusOnMount={principalInputShouldCaptureFocus}
                />
              )}
              {formik.errors.principal && formik.touched.principal && (
                <ValidationErrorMessage
                  touched={formik.touched.principal}
                  errorMessage={formik.errors.principal["id"]}
                />
              )}
            </div>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "startDato",
                  label: "Startdato",
                  isRequired: true,
                  initialValue: "",
                }}
              />
            </div>
            <h3 className="mb-n2 mt-3 flex-100">Firmarelation</h3>
            {currentFirma ? (
              <>
                <div className="flex-100">
                  <p className="form-label">
                    Personen er allerede tilknyttet firmaet:
                  </p>
                  <TicketRoot
                    className="bg-light w-100"
                    iconLeft={<FirmaIcon height={22} />}
                  >
                    <TicketHeading>
                      <>{currentFirma.firma.navn1}</>
                    </TicketHeading>
                    <TicketSub>
                      <TicketSubItem>
                        Firmanr.{" "}
                        {currentFirma.personfirmatypeNrNavigation?.navn} |
                      </TicketSubItem>
                      <TicketSubItem>
                        København |{" "}
                        {currentFirma.firma.beliggenhedsadresse.postnummer}
                      </TicketSubItem>
                      <TicketSubItem>CVR-nr. 123 4567</TicketSubItem>
                    </TicketSub>
                  </TicketRoot>
                </div>
                <div className="flex-100 d-block">
                  <p>Fortsættes i samme firma?</p>
                  <div className="d-flex">
                    <InputRadio
                      label="Ja"
                      className=""
                      id="sammeFirmaRadio1"
                      name="sammeFirmaRadio"
                      checked={values.sammeFirma === 1 ? true : false}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setFieldValue("sammeFirma", 1);
                        } else setFieldValue("harFirmaRelation", true, true);
                      }}
                    />
                    <InputRadio
                      label="Ja, men personen skal også ansættes i et firma mere"
                      className=""
                      id="sammeFirmaRadio2"
                      name="sammeFirmaRadio"
                      checked={values.sammeFirma === 2 ? true : false}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setFieldValue("sammeFirma", 2);
                        } else setFieldValue("harFirmaRelation", true, true);
                      }}
                    />
                    <InputRadio
                      label="Nej"
                      className=""
                      id="sammeFirmaRadio3"
                      name="sammeFirmaRadio"
                      checked={values.sammeFirma === 3 ? true : false}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setFieldValue("sammeFirma", 3);
                        } else setFieldValue("harFirmaRelation", false, true);
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-100">
                <label htmlFor="" className="form-label">
                  Firma*
                </label>
                {!formik.values.firma ? (
                  <AutoCompleteFirma
                    placeholder="Vælg firma..."
                    className="w-100"
                    isInvalidClass={
                      formik.touched.firma && formik.errors.firma
                        ? "is-invalid"
                        : ""
                    }
                    onSelectedItemChange={(item) => {
                      setFirmaInputShouldCaptureFocus(true);
                      setTouched({ ...formik.touched, firma: true });
                      setFieldValue("firma", item.selectedItem);
                    }}
                    includeFirstItem={false}
                    onInputBlur={() =>
                      setTouched({ ...formik.touched, firma: true })
                    }
                    focusInputOnMount={firmaInputShouldCaptureFocus}
                  />
                ) : (
                  <AutoCompleteFormSelectedItem
                    title={"Fjern firma"}
                    item={formik.values.firma}
                    onClick={() => {
                      setFirmaInputShouldCaptureFocus(true);
                      setFieldValue("firma", undefined);
                    }}
                    focusOnMount={firmaInputShouldCaptureFocus}
                  />
                )}
                <ValidationErrorMessage
                  touched={formik.touched.firma}
                  errorMessage={formik.errors.firma}
                />
              </div>
            )}
            {values.sammeFirma === 2 || values.sammeFirma === 3 ? (
              <div className="flex-100">
                <label htmlFor="" className="form-label">
                  Firma*
                </label>{" "}
                {!formik.values.firma && (
                  <AutoCompleteFirma
                    placeholder="Vælg firma..."
                    className="w-100"
                    isInvalidClass={
                      formik.touched.firma && formik.errors.firma
                        ? "is-invalid"
                        : ""
                    }
                    onSelectedItemChange={(item) => {
                      setFirmaInputShouldCaptureFocus(true);
                      setTouched({ ...formik.touched, firma: true });
                      setFieldValue("firma", item.selectedItem);
                    }}
                    onInputBlur={() =>
                      setTouched({ ...formik.touched, firma: true })
                    }
                    includeFirstItem={false}
                    focusInputOnMount={firmaInputShouldCaptureFocus}
                  />
                )}
                {formik.values.firma && (
                  <AutoCompleteFormSelectedItem
                    title={"Fjern firma"}
                    item={formik.values.firma}
                    onClick={() => {
                      setFirmaInputShouldCaptureFocus(true);
                      setFieldValue("firma", undefined);
                    }}
                    focusOnMount={firmaInputShouldCaptureFocus}
                  />
                )}
                <ValidationErrorMessage
                  touched={formik.touched.firma}
                  errorMessage={formik.errors.firma}
                />
              </div>
            ) : null}
            {values.sammeFirma === 2 ? (
              <div className="flex-100 d-block">
                <InputCheckbox
                  id="primaer"
                  label="Er primærfirma"
                  checked={values.primaer}
                  onChange={() =>
                    setFieldValue("primaer", !values.primaer, true)
                  }
                />
              </div>
            ) : null}
            {values.sammeFirma === 1 && currentFirma ? null : (
              <>
                <div className="flex-100">
                  <TextInputFormik
                    formik={formik}
                    config={{
                      component: "textinput",
                      id: "emailArbejde",
                      label: "Email, arbejde",
                      placeholder: "Indtast email",
                      initialValue: "",
                    }}
                  />
                </div>
                <div>
                  <FormikDatePicker
                    formik={formik}
                    config={{
                      component: "datepicker",
                      id: "firmaStartDato",
                      label: "Startdato",
                      isRequired: formik.values.harFirmaRelation,
                      initialValue: "",
                    }}
                  />
                </div>
                <div>
                  <Select
                    value={formik.values.firmaRelation.kode.toString()}
                    onValueChange={(value) => {
                      switch (value) {
                        case FirmaRelationKodeDict.ansat.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ansat,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.partner.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.partner,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.ejer.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ejer,
                            true,
                          );
                          break;
                        }
                      }
                    }}
                  >
                    <label className="form-label">{"Firmarelation"}</label>
                    <SelectTrigger />
                    <SelectContent>
                      <SelectItem
                        value={FirmaRelationKodeDict.ansat.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ansat.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.partner.kode.toString()}
                      >
                        {FirmaRelationKodeDict.partner.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.ejer.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ejer.beskrivelse}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {(futureFirmaRelationer.length > 0 ||
              aktiveFirmaRelationer.length > 1) && (
              <AlertMessage
                warningIcon
                heading="Tjek at firmarelationer på personens stamoplysninger er korrekte
                    efter de er autoriseret som fuldmægtig"
              >
                <ul className="fw-medium">
                  {aktiveFirmaRelationer.length > 1 && (
                    <li>
                      {person.fornavne} {person.efternavn} har mere end een
                      eksisterende firmarelation på dato for beskikkelse og
                      deponering..
                    </li>
                  )}

                  {futureFirmaRelationer.length > 0 && (
                    <li className="fw-medium">
                      {person.fornavne} {person.efternavn} har mindst een
                      fremtidig firmarelation i forhold til dato for beskikkelse
                      og deponering.
                    </li>
                  )}
                </ul>
              </AlertMessage>
            )}
          </div>

          <StickyFooter>
            <Button
              color="secondary"
              onClick={() => {
                setModalOpen(false);
                navigate("/person/" + person.id);
              }}
            >
              Annuller
            </Button>
            <Button type="submit" color="primary">
              {isLoading && <Spinner className="text-light" size={16} />}
              Gør fuldmægtig
            </Button>
          </StickyFooter>
        </fieldset>
      </form>
    </div>
  );
}
